'use client'

import { ComponentProps, MouseEvent, MouseEventHandler, useMemo, useState } from 'react'
import styled from 'styled-components'

import { HeartButton, zIndexForeground } from '@syconium/little-miss-figgy'

import { trackEvent } from '../../../lib/analytics'
import { useFavorites } from '../../_providers/FavoritesProvider.client'
import { useSession } from '../../_providers/SessionProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'

type FavoritesButtonProps = {
  /** The handle of the target product that will be added/removed from the current user's favorites. */
  productGroupHandle: string
  /** The color variant of the target product that will be added/removed from the current user's favorites. */
  color: string
  /** The fit variant of the target product that will be added/removed from the current user's favorites. */
  fit: string
  /** The size variant of the target product that will be added/removed from the current user's favorites. */
  size?: string
  /** The tracking category for the attributes that are applied to the click target. */
  trackingCategory?: string
  /** Callback for when the target product was succesfully removed from the user's favorites. */
  onRemove?: MouseEventHandler<HTMLButtonElement>
  /** Callback for when the target product was succesfully added to the user's favorites. */
  onAdd?: MouseEventHandler<HTMLButtonElement>
  /** Callback for when the favorites button is initially clicked. The second provided argument will be the current "filled" state of the target product. */
  onClick?: (event: MouseEvent<HTMLButtonElement>, filled: boolean) => void
  /** The image to be displayed in the toast notification. */
  toastImage?: string
} & ComponentProps<typeof HeartButton>

/** Button component that allows users to add/remove products from their favorites list. */
export function FavoritesButton({
  productGroupHandle,
  color,
  fit,
  size,
  trackingCategory = 'Favorites',
  onClick,
  onRemove,
  onAdd,
  toastImage,
  ...rest
}: FavoritesButtonProps) {
  const { enabled: favoritesEnabled } = useFavorites()

  const { favorites, status, removeFavorite, addFavorite } = useFavorites()

  // Manage pending state for remove/add operations
  const [pending, setPending] = useState(false)

  const {
    attributes: { visitorId },
  } = useSession()
  const translations = useTranslation()

  const matchingFavorite = useMemo(
    () =>
      favorites.find(favorite => {
        return favorite?.productGroupHandle === productGroupHandle && favorite?.color === color
      }),
    [favorites, productGroupHandle, color]
  )

  if (!favoritesEnabled) return null

  const id = matchingFavorite?.id
  const filled = !!id

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event, filled)
    try {
      setPending(true)
      if (filled) {
        if (id) {
          await removeFavorite({ id, visitorId })
          onRemove?.(event)
        }
      } else {
        const { errors, data } = await addFavorite({
          input: { productGroupHandle: productGroupHandle, color, fit, size },
          visitorId,
        })
        onAdd?.(event)
        if ((errors && errors.length > 0) || !data) {
          window.dispatchEvent(
            new CustomEvent('open-toast-notification', {
              detail: {
                type: 'favorites-error-toast-notification',
                payload: { productHandle: productGroupHandle, toastImage },
              },
            })
          )
        } else {
          window.dispatchEvent(
            new CustomEvent('open-toast-notification', {
              detail: {
                type: 'favorites-add-toast-notification',
                payload: { productHandle: productGroupHandle, toastImage },
              },
            })
          )
        }
      }
    } finally {
      setPending(false)
    }
  }

  return (
    <HeartButton
      aria-label={translations.favorites.favoriteLabel}
      onClick={handleClick}
      filled={filled}
      disabled={pending || status !== 'resolved'}
      {...trackEvent({
        action: 'click',
        category: trackingCategory,
        label: filled ? 'Remove Favorite' : 'Add Favorite',
        value: productGroupHandle,
      })}
      {...rest}
    />
  )
}

export const FavoritesButtonWrapper = styled.div`
  position: relative;
`

/** Variant of `FavoritesButton` that is absolutely positioned to an enclosing relative wrapper. */
export const FloatingFavoritesButton = styled(FavoritesButton)`
  && {
    position: absolute;
    top: ${o => o.theme.spacing(2)};
    right: ${o => o.theme.spacing(2)};
    z-index: ${zIndexForeground};
    max-width: 32px;
  }
`
